import React, { useState, useEffect, useRef } from "react";
import { IonButton } from "@ionic/react";
import {
  ReactionPickerProps,
  ReactionType,
  REACTION_EMOJIS,
  REACTION_LABELS,
} from "../../types/socialJournal";
import "./ReactionPicker.css";

const ReactionPicker: React.FC<ReactionPickerProps> = ({
  isOpen,
  onReactionSelect,
  onClose,
  triggerRef,
  activePointerId,
}) => {
  // Default to thumbsUp (Like) when picker opens
  const [hoveredReaction, setHoveredReaction] = useState<ReactionType | null>(
    "thumbsUp"
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const reactionRefs = useRef<Map<ReactionType, HTMLElement>>(new Map());
  const closeTimer = useRef<number | null>(null);
  const isMounted = useRef(true);
  // Mirrors hoveredReaction so the document listeners read the live value,
  // not the one captured when the effect subscribed.
  const hoveredReactionRef = useRef<ReactionType | null>("thumbsUp");
  // Blocks the click synthesised after pointerup from double-applying.
  const committedRef = useRef(false);

  const setHover = (type: ReactionType | null) => {
    hoveredReactionRef.current = type;
    setHoveredReaction(type);
  };

  const reactions: { type: ReactionType; color: string; textColor: string }[] =
    [
      { type: "thumbsUp", color: "#E1F2FE", textColor: "#0778CF" },
      { type: "fire", color: "#FFE0E3", textColor: "#FF001A" },
      { type: "smile", color: "#EEEBF4", textColor: "#8575B3" },
      { type: "love", color: "#E4F6FB", textColor: "#05ACD9" },
      { type: "insightful", color: "#FFF0E0", textColor: "#B65D00" },
      { type: "clap", color: "#E0FFF4", textColor: "#00B977" },
    ];

  const handleReactionClick = (reaction: ReactionType) => {
    onReactionSelect(reaction);

    // Clear any existing close timer
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
    }

    closeTimer.current = window.setTimeout(() => {
      if (isMounted.current && isOpen) {
        onClose();
        setHoveredReaction(null);
      }
    }, 150);
  };

  // Hover by x only - the reactions are a single row, so vertical travel is
  // ignored and dragging past either end clamps to that end's reaction.
  const hoverAtX = (clientX: number) => {
    const entries = Array.from(reactionRefs.current.entries());
    if (entries.length === 0) return;

    let nearest: ReactionType | null = null;
    let nearestGap = Number.POSITIVE_INFINITY;

    for (const [type, el] of entries) {
      const rect = el.getBoundingClientRect();

      if (clientX >= rect.left && clientX <= rect.right) {
        nearest = type;
        break;
      }

      const gap =
        clientX < rect.left ? rect.left - clientX : clientX - rect.right;

      if (gap < nearestGap) {
        nearestGap = gap;
        nearest = type;
      }
    }

    if (nearest) setHover(nearest);
  };

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
      }
    };
  }, []);

  // Add touch event listeners
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  // Reset to default (thumbsUp) when opening, clear when closing
  useEffect(() => {
    if (isOpen) {
      committedRef.current = false;
      hoveredReactionRef.current = "thumbsUp";
      setHoveredReaction("thumbsUp");
    } else {
      hoveredReactionRef.current = null;
      setHoveredReaction(null);
    }
  }, [isOpen]);

  // Drag-to-select. The like button holds pointer capture, so events retarget
  // to it - but they still bubble to document, the one place we see the whole
  // gesture.
  useEffect(() => {
    if (!isOpen) return;
    // Armed only while the finger is down; otherwise fall back to tap-to-select.
    if (activePointerId === null || activePointerId === undefined) return;

    const handleMove = (e: PointerEvent) => {
      if (e.pointerId !== activePointerId) return;
      hoverAtX(e.clientX);
    };

    // Lifting the finger commits whatever is hovered.
    const handleUp = (e: PointerEvent) => {
      if (e.pointerId !== activePointerId) return;

      const picked = hoveredReactionRef.current;
      committedRef.current = true;

      if (picked) {
        onReactionSelect(picked);
      }
      onClose();
    };

    // A cancel is the system taking the gesture away, not a choice.
    const handleCancel = (e: PointerEvent) => {
      if (e.pointerId !== activePointerId) return;
      onClose();
    };

    document.addEventListener("pointermove", handleMove);
    document.addEventListener("pointerup", handleUp);
    document.addEventListener("pointercancel", handleCancel);

    return () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", handleUp);
      document.removeEventListener("pointercancel", handleCancel);
    };
  }, [isOpen, activePointerId, onReactionSelect, onClose]);

  // Calculate position relative to trigger button
  const [position, setPosition] = useState({ bottom: 0, left: 0 });

  useEffect(() => {
    if (isOpen && triggerRef?.current) {
      const buttonRect = triggerRef.current.getBoundingClientRect();
      const engagementEl = triggerRef.current.closest(".post-engagement");
      const parentRect = engagementEl?.getBoundingClientRect();

      if (parentRect && engagementEl) {
        const pickerWidth = 288; // Width of reaction picker
        const buttonCenterX =
          buttonRect.left + buttonRect.width / 2 - parentRect.left;

        // Calculate left position (center above button, but constrain within bounds)
        let leftPos = buttonCenterX - pickerWidth / 2;

        // Ensure picker doesn't go beyond left edge
        if (leftPos < 10) {
          leftPos = 16;
        }

        // Ensure picker doesn't go beyond right edge
        const maxLeft = parentRect.width - pickerWidth - 10;
        if (leftPos > maxLeft) {
          leftPos = maxLeft;
        }

        // Position above the button
        const bottomPos = parentRect.bottom - buttonRect.top + 16;

        setPosition({
          bottom: bottomPos,
          left: leftPos,
        });
      }
    }
  }, [isOpen, triggerRef]);

  // Handle keyboard events (Escape to close)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement;
      if (
        !containerRef.current?.contains(target) &&
        !triggerRef?.current?.contains(target)
      ) {
        onClose();
      }
    };

    // Add delay to prevent immediate close on open
    const timer = setTimeout(() => {
      document.addEventListener("click", handleClickOutside, true);
      document.addEventListener("touchstart", handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", handleClickOutside, true);
      document.removeEventListener("touchstart", handleClickOutside, true);
    };
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="reaction-picker-backdrop"
        onClick={onClose}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClose();
          }
        }}
        aria-label="Close reaction picker"
      />

      {/* Picker container */}
      <div
        ref={containerRef}
        className="reaction-picker-container"
        style={{
          position: "absolute",
          bottom: `${position.bottom}px`,
          left: `${position.left}px`,
          zIndex: 1001,
          touchAction: "none",
        }}
      >
        {reactions.map(({ type, color, textColor }) => {
          const ReactionIcon = REACTION_EMOJIS[type];

          return (
            <div
              key={type}
              ref={(el) => {
                if (el) reactionRefs.current.set(type, el);
              }}
              className={`reaction-option-wrapper ${
                hoveredReaction === type ? "hovered" : ""
              }`}
            >
              {hoveredReaction === type && (
                <div
                  className="reaction-label-tooltip"
                  style={{
                    backgroundColor: color,
                    color: textColor,
                    borderColor: textColor,
                  }}
                >
                  {REACTION_LABELS[type]}
                </div>
              )}

              <IonButton
                fill="clear"
                className="reaction-option"
                onClick={() => {
                  // A committed drag must not be re-applied by the click
                  // that follows pointerup.
                  if (committedRef.current) return;
                  handleReactionClick(type);
                }}
                style={{
                  "--background": color,
                  "--background-hover": color,
                  "--background-activated": `${color}99`,
                }}
              >
                <div className="reaction-content">
                  <ReactionIcon
                    className="reaction-emoji"
                    aria-label={REACTION_LABELS[type]}
                  />
                </div>
              </IonButton>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ReactionPicker;
